import { useTheme } from 'styled-components';
import { Loader } from 'components/Widget/Loader';
import * as S from './styles';
import { unthemed } from 'tokens';

export enum ButtonState {
	REGULAR, // regular state, clicking performs an action
	DISABLED, // not clickable.
	DISABLED_SPINNER, // not clickable; shows an inline spinner for in-flight work
}

export enum ButtonType {
	PRIMARY,
	SECONDARY,
	NOTICEABLE,
	PUBLISH,
}

export enum ButtonSize {
	MEDIUM = 32,
}

interface ButtonProps {
	// user-facing label of the button
	label?: string;

	// if not provided, defaults to ButtonState.REGULAR
	state?: ButtonState;

	// if not provided, defaults to ButtonType.PRIMARY
	type?: ButtonType;

	// if not provided, defaults to ButtonSize.MEDIUM
	size?: ButtonSize;

	// optional width in px
	width?: number;

	// this will be called when the button is clicked, if the state is not disabled
	onSelected?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

	children?: React.ReactNode;
	'data-testid'?: string;
}

const Button = ({
	label,
	state = ButtonState.REGULAR,
	type,
	size = ButtonSize.MEDIUM,
	width,
	onSelected,
	onClick,
	children,
	'data-testid': dataTestId,
}: ButtonProps) => {
	const theme = useTheme();

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onButtonPress = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (state === ButtonState.REGULAR) {
			onSelected?.(e);
			onClick?.(e);
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	// set defaults to match PRIMARY / REGULAR config
	let backgroundColor = theme.gray[1000];
	let activeBackgroundColor = theme.gray[400];
	let hoverBackgroundColor = theme.gray[700];
	let borderColor = 'transparent';
	let labelColor = theme.gray[0];

	const isDisabled = state === ButtonState.DISABLED || state === ButtonState.DISABLED_SPINNER;

	switch (type) {
		case ButtonType.PRIMARY:
			if (isDisabled) {
				backgroundColor = theme.gray[100];
				activeBackgroundColor = theme.gray[100];
				hoverBackgroundColor = theme.gray[100];
				borderColor = theme.gray[400];
				labelColor = theme.gray[400];
			}
			break;
		case ButtonType.SECONDARY:
			if (isDisabled) {
				backgroundColor = theme.gray[0];
				activeBackgroundColor = theme.gray[0];
				hoverBackgroundColor = theme.gray[0];
				borderColor = theme.gray[200];
				labelColor = theme.gray[400];
			} else {
				backgroundColor = theme.gray[0];
				activeBackgroundColor = theme.gray[100];
				hoverBackgroundColor = theme.gray[200];
				borderColor = theme.gray[200];
				labelColor = theme.gray[1000];
			}
			break;
		case ButtonType.NOTICEABLE:
			if (isDisabled) {
				backgroundColor = theme.gray[100];
				activeBackgroundColor = theme.gray[100];
				hoverBackgroundColor = theme.gray[100];
				borderColor = theme.gray[400];
				labelColor = theme.gray[400];
			} else {
				backgroundColor = unthemed.blue['500'];
				// hardcoded blues — theme blue tokens need cleanup; safe here since unthemed
				activeBackgroundColor = unthemed.blue['700'];
				hoverBackgroundColor = unthemed.blue['600'];
				borderColor = 'transparent';
				labelColor = unthemed.gray['0'];
			}
			break;
		case ButtonType.PUBLISH:
			if (isDisabled) {
				backgroundColor = theme.gray[100];
				activeBackgroundColor = theme.gray[100];
				hoverBackgroundColor = theme.gray[100];
				borderColor = theme.gray[400];
				labelColor = theme.gray[400];
			} else {
				backgroundColor = theme.green[600];
				hoverBackgroundColor = theme.green[400];
				activeBackgroundColor = theme.green[800];
				borderColor = 'transparent';
				labelColor = theme.gray[0];
			}
			break;
	}

	return (
		<S.Base
			backgroundColor={backgroundColor}
			activeBackgroundColor={activeBackgroundColor}
			hoverBackgroundColor={hoverBackgroundColor}
			borderColor={borderColor}
			labelColor={labelColor}
			width={width ? width + 'px' : '60px'}
			height={size}
			fontSize={12}
			state={state}
			onClick={onButtonPress}
			data-testid={dataTestId}
		>
			{state === ButtonState.DISABLED_SPINNER && (
				<S.SpinnerWrap>
					<Loader size={14} />
				</S.SpinnerWrap>
			)}
			{label || children}
		</S.Base>
	);
};

export default Button;
